import { useCallback, useEffect, useState } from "react";

// Shared data layer for the three redesigned Data Seamless screens (Resumen / Pendientes /
// Datos). Spec §10 asks us to avoid duplicate fetches when several views consume the same
// dataset — completeness and the field-level gap breakdown are needed by more than one
// screen, so they are fetched ONCE here and passed down.
//
// Screen-specific datasets (assumptions/pending, translations, per-entity gaps) stay in
// their own screen; only the genuinely shared ones live here. No new state-management lib
// (§10) — plain hook state, lifted to DataSeamlessTab.
//
// Returns { summary, articles, gapFields, loading, error, reload }. `reload()` refetches
// both after a mutation or a new upload; screens call it so the whole tab stays in sync.
export const useProjectSeamlessData = (api, projectId) => {
  const [summary, setSummary] = useState({});
  const [articles, setArticles] = useState([]);
  const [gapFields, setGapFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    const [comp, gaps] = await Promise.all([
      api("GET", `/projects/${projectId}/completeness`),
      api("GET", `/projects/${projectId}/gaps?groupBy=field`),
    ]);
    setLoading(false);

    if (comp.ok) {
      setArticles(comp.data.articles ?? []);
      setSummary(comp.data.summary ?? {});
    } else {
      setArticles([]);
      setSummary({});
    }
    setGapFields(gaps.ok ? gaps.data.fields ?? [] : []);

    // Surface the first failure so screens can render an error state (§9).
    if (!comp.ok) setError(`Error ${comp.status} al cargar el estado del proyecto`);
    else if (!gaps.ok) setError(`Error ${gaps.status} al cargar el detalle de datos`);
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  return { summary, articles, gapFields, loading, error, reload: load };
};
